<template>
  <div class="c-bulk-card">
    <header class="c-bulk-card__header">
      <span class="c-bulk-card__icon">
        <app-icon name="upload" />
      </span>
      <div>
        <h3 class="c-bulk-card__title">
          Import SEO rules
        </h3>
        <p class="c-bulk-card__subtitle">
          Upload a CSV file (e.g. one created with Export) to create or update SEO rules for one language.
        </p>
      </div>
    </header>

    <div class="c-bulk-card__body">
      <div
        v-if="error && error.messages"
        class="ui-alert ui-alert--danger"
        role="alert"
      >
        <app-icon name="error" />
        <span>{{ error.messages }}</span>
      </div>

      <section class="c-bulk-card__section">
        <h4 class="c-bulk-card__section-title">
          <span class="c-bulk-card__step">1</span>
          Language
        </h4>
        <label class="c-bulk-card__field">
          <span>Rules in the file will be saved for</span>
          <select
            v-model="currentLanguageId"
            class="ui-select"
            :disabled="isSubmitting"
          >
            <option
              :value="null"
              disabled
            >
              Choose a language…
            </option>
            <option
              v-for="language in languages"
              :key="language.id"
              :value="language.id"
            >
              {{ language.name }}
            </option>
          </select>
        </label>
      </section>

      <section class="c-bulk-card__section">
        <h4 class="c-bulk-card__section-title">
          <span class="c-bulk-card__step">2</span>
          CSV file
        </h4>

        <div
          v-if="file"
          class="c-bulk-card__file"
        >
          <span class="c-bulk-card__file-icon">
            <app-icon name="description" />
          </span>
          <div class="c-bulk-card__file-meta">
            <strong>{{ file.name }}</strong>
            <small>{{ formatSize(file.size) }}</small>
          </div>
          <button
            type="button"
            class="c-bulk-card__file-remove"
            title="Remove file"
            aria-label="Remove file"
            :disabled="isSubmitting"
            @click="clearFile"
          >
            <app-icon name="close" />
          </button>
        </div>

        <label
          v-else
          :class="['c-bulk-card__dropzone', { 'is-dragging': isDragging }]"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleDrop"
        >
          <input
            ref="fileInput"
            type="file"
            accept=".csv"
            :disabled="isSubmitting"
            @change="handleFileSelect($event.target.files)"
          >
          <span class="c-bulk-card__dropzone-icon">
            <app-icon
              name="upload_file"
              :size="28"
            />
          </span>
          <strong>Click to choose a CSV file</strong>
          <small>or drag and drop it here</small>
        </label>
      </section>
    </div>

    <footer class="c-bulk-card__footer">
      <span class="c-bulk-card__summary">
        {{ !currentLanguageId ? 'Choose a language' : !file ? 'Choose a CSV file' : 'Ready to import' }}
      </span>
      <button
        type="button"
        :class="['ui-btn ui-btn--primary', { 'ui-btn--loading': isSubmitting }]"
        :disabled="isSubmitting || !file || !currentLanguageId"
        @click="handleImportSeoRules()"
      >
        <app-icon name="upload" />
        Import
      </button>
    </footer>
  </div>
</template>

<script>
import { seoRulesService } from '@/services/seo-rules'
import { responseService } from '@/services/response'
import AppIcon from '@/shared/app-icon/AppIcon'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: "ImportSeoRulesComponent",

  components: {
    AppIcon,
  },

  props: {
    website: {
      type: Object,
      default: null,
    },

    languages: {
      type: Array,
      required: true
    },

    defaultLanguageId: {
      type: Number,
      required: false,
      default: null
    },
  },

  data() {
    return {
      currentLanguageId: null,
      file: null,
      isSubmitting: false,
      isDragging: false,
    };
  },

  computed: {
    ...mapGetters("response", ["success", "error"]),
  },

  methods: {
    ...mapActions("response", ["handleSuccess", "handleError", "resetError"]),

    handleFileSelect(files) {
      this.file = (files && files[0]) || null;
    },

    handleDrop(event) {
      this.isDragging = false;

      const files = Array.from(event.dataTransfer.files || []);

      // the file picker only accepts .csv, so a dropped file must be one too
      if (!this.isSubmitting && files.length && /\.csv$/i.test(files[0].name)) {
        this.handleFileSelect(files);
      }
    },

    clearFile() {
      this.file = null;
    },

    formatSize(bytes) {
      if (bytes < 1024) {
        return `${bytes} B`;
      }

      return bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    },

    async handleImportSeoRules() {
      this.isSubmitting = true;
      try {
        await seoRulesService.import(this.website.id, this.currentLanguageId, this.file);
        this.handleSuccess({message: 'Successfully updated SEO rule'});
      } catch (err) {
        this.handleError(responseService.getErrorMessage(err.data.message));
      }
      this.isSubmitting = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './bulk-card';
</style>
